import { useMemo } from "react";
import { TableCell, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Settings, Link2, KeyRound } from "lucide-react";
import { ActionsDropdown } from "@/components/ui/actions-dropdown";
import { buildAppConfigActions } from "@/components/actions";
import { AppConfigSnapshot } from "@/services/appConfigApi";
import { useAppConfigTags } from "@/hooks/useAppConfigurations";
import { TagIcon } from "@/components/tags/TagIcon";
import { InteractiveTitle } from "@/components/ui/interactive-title";

interface AppConfigRowProps {
  config: AppConfigSnapshot;
  appName: string;
  iconUrl?: string;
  linkedToCatalog: boolean;
  onView: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

const formatDate = (iso?: string) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return "—";
  }
};

export const AppConfigRow = ({
  config,
  appName,
  iconUrl,
  linkedToCatalog,
  onView,
  onEdit,
  onDuplicate,
  onDelete,
}: AppConfigRowProps) => {
  const { data: tags = [] } = useAppConfigTags(config.policyId);
  const keyCount = config.keys.length;
  const visibleTags = useMemo(() => tags.slice(0, 3), [tags]);

  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-2 min-w-0">
          {iconUrl ? (
            <img
              src={iconUrl}
              alt=""
              className="h-7 w-7 rounded-md border shrink-0"
            />
          ) : (
            <Settings className="h-4 w-4 text-primary shrink-0" />
          )}
          <span className="font-medium truncate max-w-[200px]">{appName}</span>
        </div>
      </TableCell>
      <TableCell>
        <InteractiveTitle onClick={onView} className="text-sm max-w-[200px]">
          {config.configName}
        </InteractiveTitle>
      </TableCell>
      <TableCell>
        <span className="text-xs font-mono text-muted-foreground truncate max-w-[260px] inline-block align-middle">
          {config.bundleId || "—"}
        </span>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className="text-xs gap-1">
          <KeyRound className="h-3 w-3" />
          {keyCount}
        </Badge>
      </TableCell>
      <TableCell>
        {tags.length === 0 ? (
          <span className="text-xs text-muted-foreground">No tags</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {visibleTags.map((tag) => (
              <Badge
                key={tag.id}
                variant="secondary"
                className="gap-1 text-[10px] px-1.5 py-0"
                style={{
                  backgroundColor: `${tag.color}20`,
                  borderColor: tag.color,
                  color: tag.color,
                }}
              >
                <TagIcon iconName={tag.icon} color={tag.color} size={6} />
                {tag.name}
              </Badge>
            ))}
            {tags.length > visibleTags.length && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                +{tags.length - visibleTags.length}
              </Badge>
            )}
          </div>
        )}
      </TableCell>
      <TableCell>
        {linkedToCatalog ? (
          <Badge variant="secondary" className="text-xs gap-1">
            <Link2 className="h-3 w-3" /> Linked
          </Badge>
        ) : (
          <Badge variant="outline" className="text-xs">External</Badge>
        )}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {formatDate(config.updatedAt)}
      </TableCell>
      <TableCell className="text-right">
        <ActionsDropdown
          align="end"
          actions={buildAppConfigActions({
            surface: "list",
            onView,
            onEdit,
            onDuplicate,
            onDelete,
          })}
        />
      </TableCell>
    </TableRow>
  );
};
